import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

// Dropping someone onto someone else sets the Manager field, and manager
// rights follow that field. There is no second switch to flip afterwards.
function buildTree(rows) {
  const byUser = new Map();
  rows.forEach((e) => { if (e.user_id) byUser.set(e.user_id, e); });
  const kids = new Map();
  const roots = [];
  rows.forEach((e) => {
    const parent = e.manager_id ? byUser.get(e.manager_id) : null;
    if (parent && parent.id !== e.id) {
      if (!kids.has(parent.id)) kids.set(parent.id, []);
      kids.get(parent.id).push(e);
    } else {
      roots.push(e);
    }
  });
  const sort = (a, b) => (a.name || "").localeCompare(b.name || "");
  roots.sort(sort);
  kids.forEach((v) => v.sort(sort));
  return { roots, kids };
}

function collectDescendants(id, kids, acc) {
  const children = kids.get(id) || [];
  for (let i = 0; i < children.length; i += 1) {
    acc.add(children[i].id);
    collectDescendants(children[i].id, kids, acc);
  }
  return acc;
}

// Declared at module level, not inside the page component: a component that
// renders itself has to be a stable top-level binding.
// A component cannot name itself inside its own JSX here: the dev server's
// visual-edits Babel plugin follows that reference and blows its stack, so
// the sandbox preview will not compile even though yarn build does. Going
// through this holder keeps the recursion at runtime, where it belongs.
const NODE = { current: null };

function OrgNode(props) {
  const { person, depth, kids, dragId, overId, blocked, busy, handlers } = props;
  const children = kids.get(person.id) || [];
  const isBlocked = Boolean(dragId) && blocked.has(person.id);
  const isOver = overId === person.id && !isBlocked;
  const SelfNode = NODE.current;
  return (
    <div className="mt-2" style={{ marginLeft: depth ? 24 : 0 }}>
      <div
        draggable={!busy}
        onDragStart={() => handlers.start(person)}
        onDragEnd={handlers.end}
        onDragOver={(ev) => { ev.preventDefault(); ev.stopPropagation(); if (!isBlocked) handlers.over(person.id); }}
        onDragLeave={() => handlers.leave(person.id)}
        onDrop={(ev) => { ev.preventDefault(); ev.stopPropagation(); handlers.drop(person); }}
        data-testid={"org-node-" + person.id}
        className={[
          "flex items-center gap-3 rounded-md border px-3 py-2 bg-white cursor-grab",
          isOver ? "border-emerald-500 ring-2 ring-emerald-200" : "border-zinc-200",
          isBlocked ? "opacity-40" : "",
        ].join(" ")}
      >
        <span className="text-sm font-medium text-zinc-900">{person.name}</span>
        <span className="text-[11px] text-zinc-500">{person.employee_code}</span>
        {person.designation ? <span className="text-[11px] text-zinc-400">{person.designation}</span> : null}
        {children.length ? (
          <Badge variant="outline" className="text-[11px] border-emerald-200 text-emerald-700 bg-emerald-50">
            {children.length} report{children.length === 1 ? "" : "s"}
          </Badge>
        ) : null}
        {!person.user_id ? (
          <Badge variant="outline" className="text-[11px] border-amber-200 text-amber-700 bg-amber-50">
            no sign-in yet
          </Badge>
        ) : null}
      </div>
      {children.map((c) => (
        <SelfNode
          key={c.id}
          person={c}
          depth={depth + 1}
          kids={kids}
          dragId={dragId}
          overId={overId}
          blocked={blocked}
          busy={busy}
          handlers={handlers}
        />
      ))}
    </div>
  );
}


NODE.current = OrgNode;
export default function OrgChart() {
  const [rows, setRows] = useState(null);
  const [drag, setDrag] = useState(null);
  const [overId, setOverId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/employees", { params: { status: "active" } });
      setRows(data || []);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const tree = useMemo(() => buildTree(rows || []), [rows]);
  const blocked = useMemo(() => {
    const acc = new Set();
    if (drag) { acc.add(drag.id); collectDescendants(drag.id, tree.kids, acc); }
    return acc;
  }, [drag, tree]);

  const reassign = useCallback(async (person, managerUserId) => {
    setBusy(true);
    try {
      // "" rather than null: the API treats an empty manager as no manager.
      await api.patch("/employees/" + person.id, { manager_id: managerUserId || "" });
      toast.success(managerUserId
        ? person.name + " now reports to the new manager"
        : person.name + " moved to the top level");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); setDrag(null); setOverId(null); }
  }, [load]);

  const handlers = useMemo(() => ({
    start: (p) => setDrag(p),
    end: () => { setDrag(null); setOverId(null); },
    over: (id) => setOverId(id),
    leave: (id) => setOverId((o) => (o === id ? null : o)),
    drop: (target) => {
      setOverId(null);
      if (!drag) return;
      if (blocked.has(target.id)) {
        toast.error("That would make someone their own manager.");
        setDrag(null);
        return;
      }
      if (!target.user_id) {
        toast.error(target.name + " has no sign-in account yet, so nobody can report to them.");
        setDrag(null);
        return;
      }
      reassign(drag, target.user_id);
    },
  }), [drag, blocked, reassign]);

  if (!rows) return <div className="text-sm text-zinc-500">Loading</div>;

  return (
    <div className="space-y-4" data-testid="org-chart">
      <div>
        <h2 className="text-lg font-semibold text-zinc-900">Org chart</h2>
        <p className="text-sm text-zinc-500">
          Drag a person onto their manager. Everyone below them moves too, and the
          new manager gets their approvals, attendance and timeline straight away.
        </p>
      </div>

      <div
        onDragOver={(ev) => { ev.preventDefault(); setOverId("__top"); }}
        onDragLeave={() => setOverId((o) => (o === "__top" ? null : o))}
        onDrop={(ev) => { ev.preventDefault(); setOverId(null); if (drag) reassign(drag, null); }}
        data-testid="org-drop-top"
        className={[
          "rounded-md border border-dashed px-3 py-2 text-xs",
          overId === "__top" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-zinc-300 text-zinc-500",
        ].join(" ")}
      >
        Drop here to remove a reporting line (reports to nobody)
      </div>

      <Card className="border-zinc-200 shadow-none">
        <CardContent className="p-4">
          {tree.roots.map((p) => (
            <OrgNode
              key={p.id}
              person={p}
              depth={0}
              kids={tree.kids}
              dragId={drag ? drag.id : null}
              overId={overId}
              blocked={blocked}
              busy={busy}
              handlers={handlers}
            />
          ))}
          {!tree.roots.length ? <div className="text-sm text-zinc-500">No active people yet.</div> : null}
        </CardContent>
      </Card>

      <Button variant="outline" className="border-zinc-300" onClick={load} disabled={busy}>
        Refresh
      </Button>
    </div>
  );
}
